/**
 * Everything the printed kit sheet renders, derived from a household record.
 *
 * Pure on purpose. Every decision here has a wrong answer that only shows up
 * on paper, after it has been printed and boxed: a dangling comma where a
 * resident name should be, a compartment a household does not own, a total time
 * that contradicts the app. Keeping it out of the JSX is what makes those
 * testable without a browser.
 */

import {
  applicableCompartments,
  CARD_META,
  CLOSING_TASK,
  type ModuleKey,
} from "@/features/onboarding/compartments";
import type { AdminHouseholdDetail } from "@/lib/api";

export interface SheetCompartment {
  key: ModuleKey;
  /** "Compartment 3 - VELUX Windows" — the box's own label, not an instruction. */
  title: string;
  /** Includes the sensor placement for Air Quality, which has no row of its own. */
  minutes: number;
  image: string;
}

export interface SheetData {
  /** Headline lines, already split. Personal when we know a first name. */
  headline: readonly string[];
  /** Who the kit belongs to: the resident when known, else the household. */
  belongsTo: string;
  /** The line under it: household and address, or just the address. */
  belongsSub: string;
  /** Null when the household has no access code, which blocks the sheet. */
  code: string | null;
  /** "783 236" — grouped for reading off paper. */
  codeDisplay: string;
  /** Bare host, for the "open this in your browser" fallback. */
  domain: string;
  /** What the QR encodes. Null when there is no code to encode. */
  qrUrl: string | null;
  compartments: readonly SheetCompartment[];
  /** The sentence under "What's in your box". Reads correctly at any count. */
  boxIntro: string;
  /** "about one hour" — never a digit, never written down. */
  totalTimeWords: string;
  /** Seeds the print dialog's filename. Household, never the resident. */
  documentTitle: string;
}

/** Small counts, spelled out. Digits in this product are addresses. */
const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five"] as const;

/**
 * Total install time as words, rounded to the nearest half hour.
 *
 * The lede is promising an evening's commitment, not a stopwatch, so the
 * smallest household (hub only, five minutes) still reads "about half an hour".
 * Bands are inclusive of their lower bound.
 */
const TIME_BANDS: readonly { upTo: number; words: string }[] = [
  { upTo: 44, words: "about half an hour" },
  { upTo: 74, words: "about one hour" },
  { upTo: 104, words: "about an hour and a half" },
  { upTo: 134, words: "about two hours" },
];
const LONGEST_TIME_WORDS = "about two and a half hours";

/**
 * Build everything the sheet renders.
 *
 * @param household The admin household record, as returned by `useAdminHousehold`.
 * @param origin Absolute origin the QR should point at, normally
 *     `window.location.origin`. Passed in rather than read from `window` so
 *     this stays pure, and correct by construction: the admin is already
 *     browsing the same deployment the household will land on.
 * @returns The sheet's content. Never throws on missing fields.
 */
export function buildSheet(household: AdminHouseholdDetail, origin: string): SheetData {
  const resident = (household.contact_name ?? "").trim();
  const firstName = resident.split(/\s+/)[0] ?? "";
  const householdName = household.name.trim();
  const addressLine = formatAddress(household);

  const keys = applicableCompartments(household.preconfig?.systems);
  const compartments: SheetCompartment[] = keys.map((key, index) => ({
    key,
    // The sheet names compartments, not actions. On paper the reader is standing
    // over the box, so the row has to match what is printed on the lid; the app
    // is where the same module becomes an instruction ("Pair VELUX Windows").
    //
    // The position is baked into this string and kept nowhere else. It used to
    // sit on an `order` field too, back when a numbered badge rendered it, and
    // two sources for one number is how a renumbered box ends up disagreeing
    // with itself.
    title: `Compartment ${index + 1} - ${CARD_META[key].label}`,
    // Placing the sensors is the back half of compartment 2, not a step of its
    // own, so its minutes ride along with Air Quality instead of being dropped.
    // The work still happens either way and the total has to stay honest: drop
    // them and the sheet would promise three quarters of an hour for an hour's
    // job.
    minutes: CARD_META[key].minutes + (key === "air_sensors" ? CLOSING_TASK.minutes : 0),
    image: CARD_META[key].image,
  }));

  const totalMinutes = compartments.reduce((sum, c) => sum + c.minutes, 0);

  const code = (household.access_code ?? "").trim() || null;

  return {
    headline: firstName ? [`${firstName},`, "welcome to", "your kit"] : ["Welcome to", "your kit"],
    belongsTo: resident || householdName,
    // With a resident named, the household record is the useful second line;
    // without one it is already the first, so it must not repeat.
    belongsSub: [resident ? householdName : "", addressLine].filter(Boolean).join(", "),
    code,
    codeDisplay: formatCode(code),
    domain: hostOf(origin),
    // Encoded, not interpolated raw. Codes are generated as six digits, but
    // `access_code` is a text column and `adminImportHousehold` can carry
    // whatever a payload contains, so a stray `&` or `#` would otherwise
    // silently truncate the link the whole kit depends on.
    qrUrl: code
      ? `${origin.replace(/\/+$/, "")}/onboarding?code=${encodeURIComponent(code)}`
      : null,
    compartments,
    boxIntro: boxIntro(compartments.length),
    totalTimeWords: timeWords(totalMinutes),
    documentTitle: `velux-kit-sheet-${slugify(householdName)}`,
  };
}

/**
 * The sentence under "What's in your box".
 *
 * A household can end up with one compartment (every system switched off but the
 * hub), and "One compartment, in order. The app tells you which one to open
 * next." is nonsense: there is no order and no next. Count is spelled out, per
 * the house rule that digits are addresses.
 *
 * Exported for its own test.
 */
export function boxIntro(count: number): string {
  const word = COUNT_WORDS[count] ?? String(count);
  const resume = "You can pause at any point and pick up where you left off.";
  if (count === 1) {
    return `${word} compartment. ${resume}`;
  }
  return `${word} compartments, in order. ${resume} The app tells you which one to open next.`;
}

/** Total time as words. Exported for its own test. */
export function timeWords(minutes: number): string {
  return TIME_BANDS.find((band) => minutes <= band.upTo)?.words ?? LONGEST_TIME_WORDS;
}

/**
 * The address as one line, from four independently nullable fields.
 *
 * Any of them can be empty, including all four, in which case this returns an
 * empty string and the caller drops the line rather than printing a stray
 * comma.
 */
function formatAddress(household: AdminHouseholdDetail): string {
  const locality = [household.postcode, household.city]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return [household.address, locality, household.country_code]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

/** Six digits split in two, so it can be read off paper without losing place. */
function formatCode(code: string | null): string {
  if (!code) {
    return "";
  }
  return /^\d{6}$/.test(code) ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

/** Bare host for display. Falls back to the raw string if it will not parse. */
function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  }
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "household"
  );
}
